import React from 'react';
import { MapContainer, TileLayer, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon issue in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Use the same pin SVGs from ImageComponent
const greenPinSvg = encodeURIComponent(`
  <svg width="30" height="36" viewBox="0 0 37 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.5 2C10.5 2 4 8.5 4 16.5C4 28.5 18.5 42 18.5 42C18.5 42 33 28.5 33 16.5C33 8.5 26.5 2 18.5 2Z" fill="#3FA43F" stroke="white" stroke-width="1"/>
    <circle cx="18.5" cy="16.5" r="5" fill="white"/>
  </svg>
`);

const redPinSvg = encodeURIComponent(`
  <svg width="30" height="36" viewBox="0 0 37 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.5 2C10.5 2 4 8.5 4 16.5C4 28.5 18.5 42 18.5 42C18.5 42 33 28.5 33 16.5C33 8.5 26.5 2 18.5 2Z" fill="#FF3630" stroke="white" stroke-width="1"/>
    <circle cx="18.5" cy="16.5" r="5" fill="white"/>
  </svg>
`);

const greyPinSvg = encodeURIComponent(`
  <svg width="30" height="36" viewBox="0 0 37 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.5 2C10.5 2 4 8.5 4 16.5C4 28.5 18.5 42 18.5 42C18.5 42 33 28.5 33 16.5C33 8.5 26.5 2 18.5 2Z" fill="#85868E" stroke="white" stroke-width="1"/>
    <circle cx="18.5" cy="16.5" r="5" fill="white"/>
  </svg>
`);

const orangePinSvg = encodeURIComponent(`
  <svg width="30" height="36" viewBox="0 0 37 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.5 2C10.5 2 4 8.5 4 16.5C4 28.5 18.5 42 18.5 42C18.5 42 33 28.5 33 16.5C33 8.5 26.5 2 18.5 2Z" fill="#FFBF00" stroke="white" stroke-width="1"/>
    <circle cx="18.5" cy="16.5" r="5" fill="white"/>
  </svg>
`);

const GreenIcon = L.icon({
  iconUrl: "data:image/svg+xml," + greenPinSvg,
  iconSize: [30, 36],
  iconAnchor: [15, 36],
});

const RedIcon = L.icon({
  iconUrl: "data:image/svg+xml," + redPinSvg,
  iconSize: [30, 36],
  iconAnchor: [15, 36],
});

const OrangeIcon = L.icon({
  iconUrl: "data:image/svg+xml," + orangePinSvg,
  iconSize: [30, 36],
  iconAnchor: [15, 36],
});

const GreyIcon = L.icon({
  iconUrl: "data:image/svg+xml," + greyPinSvg,
  iconSize: [30, 36],
  iconAnchor: [15, 36],
});

// Same logic as ImageComponent to determine pin color
const getMarkerIcon = (compliance: string, connectivity: string) => {
  const getStatusPriority = (status: string) => {
    switch (status) {
      case "NonCompliant":
      case "Offline":
        return 3;
      case "PartiallyCompliant":
      case "Partial":
        return 2;
      case "Compliant":
      case "Online":
        return 1;
      default:
        return 0; // Unknown or other statuses
    }
  };

  // Get the worst status between compliance and connectivity
  const compliancePriority = getStatusPriority(compliance);
  const connectivityPriority = getStatusPriority(connectivity);
  const worstPriority = Math.max(compliancePriority, connectivityPriority);

  // Return icon based on the worst status
  switch (worstPriority) {
    case 3: // NonCompliant or Offline
      return RedIcon;
    case 2: // PartiallyCompliant or PartialConnected
      return OrangeIcon;
    case 1: // Compliant or Online
      return GreenIcon;
    default: // Unknown or other
      return GreyIcon;
  }
};

interface GpsData {
  lat: number;
  long: number;
  alt: number;
  accuracy: number;
  speed: number;
  speedAccuracy: number;
  heading: number;
  lockTime: string;
}

interface Props {
  lat: number;
  lng: number;
  markerName?: string;
  compliance?: string;
  connectivity?: string;
  selectedGroupId?: number | null;
  onMarkerSelect?: (groupId: number | null) => void;
  firstGroupId?: number | null;
  gpsData?: GpsData | null;
  assetData?: any; // Optional asset data to get group compliance/connectivity
}

const MapComponent: React.FC<Props> = ({ 
  lat, 
  lng, 
  markerName = "Location",
  compliance = "Unknown",
  connectivity = "Unknown",
  selectedGroupId = null,
  onMarkerSelect,
  firstGroupId = null,
  gpsData = null,
  assetData = null
}) => {
  // Determine compliance and connectivity - use first group if available, otherwise use asset level
  let finalCompliance = compliance;
  let finalConnectivity = connectivity;
  
  // Only use assetData if it's available and has the necessary structure
  if (assetData?.assetConfigurationDetails?.groups && assetData.assetConfigurationDetails.groups.length > 0) {
    // Use the first group's compliance and connectivity (same logic as ImageComponent)
    const firstGroup = assetData.assetConfigurationDetails.groups[0];
    finalCompliance = firstGroup.complianceStatus || compliance || "Unknown";
    finalConnectivity = firstGroup.connectivityStatus || connectivity || "Unknown";
  }
  
  // Validate coordinates - don't render map if coordinates are invalid (0,0 or NaN)
  const isValidPosition = lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng);
  
  // Use GPS data coordinates if available, otherwise use provided lat/lng
  const finalLat = gpsData?.lat ?? lat;
  const finalLng = gpsData?.long ?? lng;
  const hasValidGpsData = gpsData && gpsData.lat !== 0 && gpsData.long !== 0 && !isNaN(gpsData.lat) && !isNaN(gpsData.long);
  const shouldRender = isValidPosition || hasValidGpsData;
  
  const position: [number, number] = shouldRender ? [finalLat, finalLng] : [0, 0];
  const icon = getMarkerIcon(finalCompliance, finalConnectivity);
  const isSelected = firstGroupId !== null && selectedGroupId === firstGroupId;

  const handleMarkerClick = () => {
    if (onMarkerSelect && firstGroupId !== null) {
      if (isSelected) {
        onMarkerSelect(null); // Deselect
      } else {
        onMarkerSelect(firstGroupId); // Select first group
      }
    }
  };

  // Don't render map if we don't have valid coordinates
  if (!shouldRender) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-gray-500">Loading GPS data...</div>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <MapContainer
        key={`map-${finalLat}-${finalLng}`} // Force re-render when coordinates change
        center={position}
        zoom={13}
        scrollWheelZoom={true}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker 
          position={position} 
          icon={icon}
          eventHandlers={firstGroupId !== null ? {
            click: handleMarkerClick
          } : {}}
          {...{ opacity: selectedGroupId === null ? 1 : (isSelected ? 1 : 0.4) } as any}
        >
          <Tooltip 
            direction="left" 
            offset={[-10, -10]}
            className={isSelected ? "selected-marker-tooltip" : ""}
          >
            <div className={`text-center ${isSelected ? 'text-[#305FE6]' : ''}`}>
              <div className="font-semibold mb-1">{markerName}</div>
              {gpsData && (
                <div className="text-xs text-left space-y-0.5 mt-2">
                  <div><strong>Latitude:</strong> {gpsData.lat.toFixed(6)}</div>
                  <div><strong>Longitude:</strong> {gpsData.long.toFixed(6)}</div>
                  <div><strong>Altitude:</strong> {gpsData.alt.toFixed(2)} m</div>
                  <div><strong>Accuracy:</strong> {gpsData.accuracy.toFixed(2)} m</div>
                  <div><strong>Speed:</strong> {gpsData.speed.toFixed(2)} m/s</div>
                  <div><strong>Speed Accuracy:</strong> {gpsData.speedAccuracy.toFixed(2)} m/s</div>
                  <div><strong>Heading:</strong> {gpsData.heading.toFixed(2)}°</div>
                  <div><strong>Lock Time:</strong> {new Date(gpsData.lockTime).toLocaleString()}</div>
                </div>
              )}
            </div>
          </Tooltip>
        </Marker>
      </MapContainer>
    </div>
  );
};

export default MapComponent;
